/**
 * client/src/pages/Reveal.tsx — the payoff. §3.13. **Built now, exercised in P3.**
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE SENTENCE IS AUTHORED BY THE PERSON WHO ASKED FOR THE VOICE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * "Before she starts, on the same laptop or from his own, Karti types one sentence he wants to hear
 * in his mother's voice. She never sees it. At the end, it plays — with the words on screen as they
 * play, so she is never surprised by speech attributed to her."
 *
 * "*'Karti, I'm proud of you'*, in his mother's voice, while he is on the call, is the entire product
 * in eight seconds. It costs one text input and one column."
 *
 * The alternative this refuses is the one every implementation reaches for by default:
 *
 *   "If the reveal instead generated a line from `script.ts` or a neutral test sentence, the moment
 *   would be a technology demo — her voice reading a stranger's words about a river — which is
 *   uncanny rather than moving, and **which invites her to evaluate fidelity, the exact comparison a
 *   first clone loses.**"
 *
 * So there is no fallback sentence in this file. If `revealText` is absent, the payoff does not
 * happen and the deferred ending does — because a payoff with a sentence nobody wrote is worse than
 * no payoff, and §3.14's ending is warm and true.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE EXISTS BEFORE THERE IS A GPU
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * spark-1 is down and stays down for this build, so nothing below has ever run against real audio.
 * It is written now, in full, for the reason §0.1 gives about seams: the *shape* of the payoff
 * decides what `POST /complete` has to return, and discovering in P3 that the screen needed the
 * sentence text and the audio in one response — while a family is sitting in front of it — is the
 * failure. `GET /api/v1/sessions/:id/reveal` (see `lib/api.ts`) exists because of this screen.
 *
 * **Nothing here is faked.** There is no sample audio, no mock sentence, and no "coming soon". If
 * the route answers 404 the page routes back to the wait screen, which owns the deferred ending.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * AFTER THE PAYOFF, IN THIS ORDER, AND NOTHING ELSE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * §3.13: "After the reveal, and only after it, in this order: the offer to save it (accounts), the
 * claim link, the offer of a longer session, and nothing else."
 *
 * Two of those three are here. **The longer session is deliberately absent**, and this is the law and
 * the reason it is deferred rather than a gap: §15 ships "the long session as a post-reveal upsell"
 * in **P6**, and there is no route that turns "yes, a few more" into more prompts against an existing
 * voice. A button that reads "Yes, a few more" and does nothing is a worse outcome on this screen
 * than no button, because this is the screen where she has just been told the thing worked.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT HAPPENS IF IT SOUNDS WRONG
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * The brief's pass/fail test is *"if the first thing she hears is a mediocre clone of herself, the
 * product has failed"*, and on ten seconds of a laptop microphone that will happen to somebody on
 * some evening. The screen shipped with exactly one answer to it — a destructive button next to the
 * player — which frames a bad clone as *her* problem to dispose of.
 *
 * So there is a quiet link, `FAMILY.payoff.notRight`, and behind it a panel that takes the blame in
 * the first person, states the true mechanism in one clause (it had a few seconds of her to go on),
 * and offers only actions that exist:
 *
 *   · **delete really deletes**, immediately, the audio and the row;
 *   · **leaving it is true** — the recordings are on the origin, VOX-LAW 26 exempts an unclaimed
 *     session from every sweep, and the clone lane can be run again on the same bytes without her
 *     saying another word.
 *
 * ⚠️ **There is no "try again" and no "record it again".** Nothing on the family path can re-run the
 * clone (it is Karti's lane, on a serialized GPU, behind one admission token — VOX-LAW 30), and
 * offering "say it all again" as the answer to "that doesn't sound like me" would be the product
 * agreeing that the fault was hers. `Studio.tsx`'s own `<Payoff>` carries the identical panel, and
 * the two must stay identical: they are the same moment, one live and one reached from a link.
 *
 * ⚠️ **Nothing on this screen ever plays her own raw recording** (§3.10, rated fatal). Not as a
 * comparison, not behind a link. The only audio element points at the generated sentence.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FAMILY } from "../../../shared/voice/copy.ts";
import type { SessionLinks } from "../../../shared/protocol.ts";
import type { RevealView } from "../lib/api.ts";
import { ApiError, api, describeForConsole } from "../lib/api.ts";
import { emailClaimLink } from "../lib/claimLink.ts";
import { useSessionHandle } from "../lib/handle.ts";
import { say } from "../lib/say.ts";
import { useSession } from "../lib/session.tsx";
import { Announce, Blank, Button, CopyButton, Field, Notice, Stage } from "../ui/primitives.tsx";

export function Reveal() {
  /**
   * ⚠️ **`:sessionId` is a `ses_` id or a `vox_` CLAIM TOKEN** — `<Claim>` navigates here with the
   * token, deliberately, because that link has to keep working on a browser with no cookie at all.
   * The server refuses anything that is not a session id, so the segment is resolved before it is
   * used as one, and the token rides along on every later call.
   */
  const { sessionId: handle = "" } = useParams();
  const { sessionId, claimToken, notFound } = useSessionHandle(handle, "reveal");
  const navigate = useNavigate();
  const { app, signedIn } = useSession();

  const [reveal, setReveal] = useState<RevealView | null>(null);
  const [links, setLinks] = useState<SessionLinks | null>(null);
  const [deleted, setDeleted] = useState(false);
  const [blocked, setBlocked] = useState(false);
  /** She pressed "That doesn't sound like me". See the header — this is a designed path, not an error. */
  const [notRight, setNotRight] = useState(false);
  const [keepDismissed, setKeepDismissed] = useState(false);
  const [email, setEmail] = useState("");
  const [emailed, setEmailed] = useState(false);

  const audio = useRef<HTMLAudioElement | null>(null);
  const token = useRef<HTMLParagraphElement | null>(null);

  const helper = app?.helperName ?? null;

  useEffect(() => {
    if (sessionId === null) return;
    const ac = new AbortController();
    // The wait screen keeps whatever the person arrived with — a claim link must stay a claim link
    // across the hop, or it stops working the moment the cookie does.
    const onward = `/s/${encodeURIComponent(handle)}/wait`;
    api.reveal(sessionId, ac.signal, claimToken).then(setReveal, (err: unknown) => {
      if (err instanceof DOMException && err.name === "AbortError") return;
      /**
       * VOX-LAW 18: routes refuse 404, never 403, so "not ready" and "not yours" are the same
       * answer here and must be treated the same. Either way the wait screen is the right place:
       * it owns the deferred ending, the claim link and the retention promise, and it re-polls.
       */
      if (err instanceof ApiError && err.notFound) {
        navigate(onward, { replace: true });
        return;
      }
      const d = describeForConsole(err);
      console.error(`reveal: fetch failed at ${d.where}: ${d.message}`);
      navigate(onward, { replace: true });
    });
    api.links(sessionId, ac.signal, claimToken).then(setLinks, () => {
      // The claim link is a section of this page, not the page. Losing it is not a reason to move
      // her off the one screen this whole product exists to reach.
    });
    return () => ac.abort();
  }, [sessionId, claimToken, handle, navigate]);

  /**
   * It plays by itself, and when the browser refuses, it says so with a button rather than silence.
   *
   * Chrome and Safari block audible autoplay without a recent user gesture, and a client-side route
   * change does not carry one. This is not a corner case: the navigation from the wait screen is
   * automatic (§3.12's countdown ends and the payoff replaces it), so the *normal* path arrives here
   * with no gesture at all.
   *
   * ⚠️ **Muted autoplay is not the workaround.** It is the standard trick and it is wrong here: the
   * only thing on this screen is eight seconds of somebody's mother's voice, and playing it silently
   * so that the element reports success would mean the payoff happened and nobody heard it.
   *
   * So: try, and on rejection surface a real button. `FAMILY.room.chimePlay` is the word "Play",
   * borrowed across groups deliberately — `FAMILY.payoff` has only "Play it again", which is wrong
   * before anything has played. It is the right single word and it is in `copy.ts`; if §3.16's
   * read-aloud dislikes the borrow, the fix is one new string in that file and one line here.
   */
  useEffect(() => {
    const el = audio.current;
    if (!el || !reveal || deleted) return;
    el.play().then(
      () => setBlocked(false),
      () => setBlocked(true),
    );
  }, [reveal, deleted]);

  const playAgain = useCallback(() => {
    const el = audio.current;
    if (!el) return;
    el.currentTime = 0;
    el.play().then(
      () => setBlocked(false),
      () => setBlocked(true),
    );
  }, []);

  /**
   * §3.13: a "no — delete that" button "that actually deletes, immediately, the audio and the row."
   *
   * **No confirmation dialog, and that is the design.** "Are you sure?" on this screen is the product
   * arguing with somebody who has just heard a machine say something in her own voice and did not
   * like it. The one moment a person is most entitled to an immediate, unquestioned no is this one.
   *
   * The optimistic local flip happens first so the words leave the screen on the press rather than
   * on the round trip; a failed request is logged for Karti and nothing is said to her, because the
   * sentence "we couldn't delete it" is the worst thing this screen could produce.
   */
  const remove = useCallback(() => {
    setDeleted(true);
    audio.current?.pause();
    // There is nothing on this screen to delete before the session resolved, so there is nothing to
    // send either. The local flip above still stands: she pressed it, and the words are gone.
    if (sessionId === null) return;
    api.deleteReveal(sessionId, claimToken).catch((err: unknown) => {
      const d = describeForConsole(err);
      console.error(`reveal: delete failed at ${d.where}: ${d.message}`);
    });
  }, [sessionId, claimToken]);

  const selectToken = useCallback(() => {
    const node = token.current;
    if (!node) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  /**
   * §2.4's "email this to yourself". Her own mail client — see `lib/claimLink.ts` for why there is
   * no request in front of it, and why there cannot be one until something in this product can send
   * mail at all.
   */
  const sendEmail = useCallback(() => {
    if (links && emailClaimLink(email, links.claimUrl)) setEmailed(true);
  }, [email, links]);

  /**
   * ⚠️ **The unresolvable handle, which used to fall through to a permanently blank page.**
   *
   * The 404 path *inside* the reveal fetch is handled — it navigates to the wait screen, which owns
   * the deferred ending. What was not handled is the handle itself failing to resolve: `sessionId`
   * stays null, the effect never runs, `reveal` stays null, and this line renders `<Blank />` for
   * ever. Measured: one single colour across the whole screenshot, no text, no control, on
   * `/s/:sessionId/hear` in any browser without the cookie. `lib/handle.ts` says the merge is
   * forbidden; `Wait.tsx` carries the identical branch for the identical reason.
   */
  if (notFound) {
    return (
      <Stage>
        <h1 className="vox-say">{FAMILY.back.title}</h1>
        <Notice>{helper ? say(FAMILY.back.notReady, { helper }) : FAMILY.mishap.keepGoing}</Notice>
        <p className="vox-quiet">{FAMILY.keep.retention}</p>
      </Stage>
    );
  }

  if (!reveal || !helper) return <Blank />;

  return (
    <Stage top>
      <h1 className="vox-say">{FAMILY.payoff.done}</h1>

      {deleted ? (
        // "Gone. Nothing kept." and then nothing else on the screen about it. No undo, no "you can
        // record another" — she asked for it to be gone and the screen agrees with her completely.
        <>
          <p className="vox-lead">{FAMILY.payoff.deleted}</p>
          <Announce>{FAMILY.payoff.deleted}</Announce>
        </>
      ) : (
        <>
          <p className="vox-lead">{say(FAMILY.payoff.intro, { helper })}</p>

          {/*
            §3.13 asks for the words on screen "so she is never surprised by speech attributed to
            her". This line applies the same requirement to the channel §3.13 forgot: a person who
            has read the sentence has still been ambushed by *hearing* herself say it. One line of
            warning, above, before the audio starts.
          */}
          <p className="vox-quiet">{FAMILY.payoff.listen}</p>

          {/*
            The words themselves, in full, from the moment the page renders — before a sound comes
            out of the laptop. There is no word-by-word highlight, because there are no word timings,
            and a fake karaoke that drifts off the audio is a worse artefact than none.

            `.vox-read` — the book face, the one the prompts are set in. It is the sentence somebody
            wrote for her, not the app talking.
          */}
          <blockquote className="vox-read">{reveal.text}</blockquote>

          {/*
            `controls` is deliberately off and `preload="auto"` is on. A native control bar puts a
            scrubber, a volume slider and a download menu on the one screen that should hold a
            sentence and two buttons; `preload` is what makes the play attempt below succeed on the
            first try rather than after a buffering gap in the middle of eight seconds.
          */}
          <audio ref={audio} src={reveal.audioUrl} preload="auto" />

          <div className="vox-btn-row">
            <Button variant="primary" onClick={playAgain}>
              {/*
                `FAMILY.payoff.play` — one word, and it is a real string in `copy.ts` now rather than
                `room.chimePlay` borrowed from the volume check. "Play it again" is wrong before
                anything has played, which is exactly the state the browser leaves this screen in
                when it refuses audible autoplay to a client-side navigation carrying no gesture.
              */}
              {blocked ? FAMILY.payoff.play : FAMILY.payoff.playAgain}
            </Button>
          </div>

          {/*
            The quiet way out, and deliberately not a second button beside "Play it again". A row of
            two equal controls, one of which destroys the thing she is listening to, presents
            deleting as an equally likely next move and frames the moment as an assessment. See the
            header for why this panel offers what it offers and refuses a "try again".
          */}
          {/*
            §3.13 puts "no — delete that" **beside the player as well as inside the panel**, and only
            the panel had it. The stated reason is that the moment she most wants it is the moment
            after she has said the thing did not sound like her — but the other half is simpler: some
            people just want it gone, and requiring them to first declare that it was bad is the
            product extracting an admission before it will obey.

            It is a `.vox-link` and not a second button in the row above: a row of two equal controls,
            one of which destroys the thing she is listening to, presents deleting as an equally
            likely next move and frames the moment as an assessment.
          */}
          <button type="button" className="vox-link" onClick={remove}>
            {FAMILY.payoff.delete}
          </button>

          {notRight ? (
            <section className="vox-card" aria-labelledby="notright-title">
              <h2 id="notright-title" className="vox-lead">
                {FAMILY.payoff.notRightTitle}
              </h2>
              <p className="vox-quiet">{say(FAMILY.payoff.notRightBody, { helper })}</p>
              <div className="vox-btn-row">
                <Button variant="quiet" onClick={() => setNotRight(false)}>
                  {say(FAMILY.payoff.leave, { helper })}
                </Button>
                <Button variant="destructive" onClick={remove}>
                  {FAMILY.payoff.delete}
                </Button>
              </div>
            </section>
          ) : (
            <button type="button" className="vox-link" onClick={() => setNotRight(true)}>
              {FAMILY.payoff.notRight}
            </button>
          )}
        </>
      )}

      {/*
        ════════════════════════════════════════════════════════════════════════
        AFTER THE PAYOFF, IN §3.13's ORDER
        ════════════════════════════════════════════════════════════════════════
        1. The offer to save it. Not shown to somebody already signed in — for them the voice is
           already attached to an account and asking again is a question with no answer. Not shown
           after a delete either: there is nothing left to keep.
      */}
      {!deleted && !signedIn && !keepDismissed && (
        <section className="vox-card" aria-labelledby="keep-title">
          <h2 id="keep-title" className="vox-lead">
            {FAMILY.keep.offerTitle}
          </h2>
          <p className="vox-quiet" style={{ marginTop: "var(--space-2)" }}>
            {FAMILY.keep.offerBody}
          </p>
          <div className="vox-btn-row" style={{ marginTop: "var(--space-4)" }}>
            {/*
              ⚠️ **`from` is the kept screen, not this one.** It used to be `pathname`, which sent
              her back to the payoff she had already heard — and when the state was lost, to
              `/voices`, the engineering console. `<Kept>` is the ending §3.13's ordering promises:
              her voice is saved, here is what happens next, here is the link again. `claimGuest`
              has already attached the voice to the new account by the time it renders, so the
              sentence it opens with is true before it is said.
            */}
            <Button
              variant="primary"
              onClick={() =>
                navigate("/signin", {
                  // The CLAIM TOKEN, in preference to whatever segment this page was reached by:
                  // `<Kept>` resolves ownership through `/c/:token`, which is the only route a token
                  // opens on its own and the only projection that carries `ownerUserId`.
                  state: { from: `/s/${encodeURIComponent(links?.claimToken ?? handle)}/kept` },
                })
              }
            >
              {FAMILY.keep.offerYes}
            </Button>
            {/*
              "Not now" dismisses the card and nothing else. It does not navigate, it does not warn,
              and it does not come back — §2.4's whole architecture exists so that declining an
              account costs her nothing: the claim link below is the durable handle, VOX-LAW 26
              exempts an unclaimed session from every sweep, and an unclaimed voice is reported on
              `/admin` with an age rather than a deadline.
            */}
            <Button variant="quiet" onClick={() => setKeepDismissed(true)}>
              {FAMILY.keep.offerLater}
            </Button>
          </div>
        </section>
      )}

      {/* 2. The claim link. VOX-LAW 20 — see `pages/Wait.tsx` for the full note; it is the same
             section, on the other ending, for the same reason. */}
      {!deleted && links && (
        <section className="vox-card" aria-labelledby="link-title">
          <h2 id="link-title" className="vox-lead">
            {FAMILY.keep.linkTitle}
          </h2>
          <p className="vox-quiet" style={{ marginTop: "var(--space-2)" }}>
            {FAMILY.keep.linkBody}
          </p>

          <p className="vox-token" ref={token} style={{ marginTop: "var(--space-4)" }}>
            {links.claimUrl}
          </p>

          <div className="vox-copyrow" style={{ marginTop: "var(--space-4)" }}>
            <CopyButton
              value={links.claimUrl}
              label={FAMILY.keep.linkCopy}
              copiedLabel={FAMILY.keep.linkCopied}
              onFail={selectToken}
            />
          </div>

          <div style={{ marginTop: "var(--space-5)" }}>
            <Field
              label={FAMILY.keep.linkEmailLabel}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <div className="vox-btn-row" style={{ marginTop: "var(--space-3)" }}>
              <Button onClick={sendEmail}>{emailed ? FAMILY.keep.linkEmailSent : FAMILY.keep.linkEmailButton}</Button>
            </div>
          </div>

          <p className="vox-quiet" style={{ marginTop: "var(--space-5)" }}>
            {FAMILY.keep.retention}
          </p>
        </section>
      )}

      {/* 3. The offer of a longer session — P6. See this file's header for the law and the reason
             it is deferred. Nothing is drawn here, on purpose. */}
    </Stage>
  );
}
